import { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowLeft } from "lucide-react"
import { useTrainingSession, useCreateTrainingSession, useUpdateTrainingSession } from "@/hooks/use-training"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"

const schema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  description: z.string().optional(),
  training_type: z.string().min(1, "Wybierz typ"),
  location: z.string().optional(),
  instructor_name: z.string().optional(),
  instructor_email: z.string().email("Nieprawidłowy email").optional().or(z.literal("")),
  starts_at: z.string().min(1, "Podaj datę rozpoczęcia"),
  ends_at: z.string().min(1, "Podaj datę zakończenia"),
  max_participants: z.coerce.number().int().positive().optional().or(z.literal(0).transform(() => undefined)),
  duration_hours: z.coerce.number().int().positive().optional().or(z.literal(0).transform(() => undefined)),
  cost_per_person: z.coerce.number().min(0).optional().or(z.literal(0).transform(() => undefined)),
  notes: z.string().optional(),
  agenda: z.string().optional(),
  status: z.string().optional(),
})

type FormData = z.infer<typeof schema>

export function TrainingFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id

  const { data: sessionData, isLoading } = useTrainingSession(id || "")
  const createSession = useCreateTrainingSession()
  const updateSession = useUpdateTrainingSession()

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      training_type: "internal",
      location: "",
      instructor_name: "",
      instructor_email: "",
      starts_at: "",
      ends_at: "",
      max_participants: 0,
      duration_hours: 0,
      cost_per_person: 0,
      notes: "",
      agenda: "",
      status: "planned",
    },
  })

  useEffect(() => {
    if (sessionData?.data) {
      const s = sessionData.data
      form.reset({
        name: s.name,
        description: s.description || "",
        training_type: s.training_type,
        location: s.location || "",
        instructor_name: s.instructor_name || "",
        instructor_email: s.instructor_email || "",
        starts_at: s.starts_at ? s.starts_at.slice(0, 16) : "",
        ends_at: s.ends_at ? s.ends_at.slice(0, 16) : "",
        max_participants: s.max_participants || 0,
        duration_hours: s.duration_hours || 0,
        cost_per_person: s.cost_per_person || 0,
        notes: s.notes || "",
        agenda: s.agenda || "",
        status: s.status,
      })
    }
  }, [sessionData])

  const onSubmit = (data: FormData) => {
    const payload = { ...data }
    if (!payload.max_participants) delete payload.max_participants
    if (!payload.duration_hours) delete payload.duration_hours
    if (!payload.cost_per_person) delete payload.cost_per_person
    if (!payload.instructor_email) delete payload.instructor_email

    if (isEditing) {
      updateSession.mutate(
        { id: id!, data: payload },
        {
          onSuccess: () => {
            toast.success("Szkolenie zaktualizowane")
            navigate(`/training/${id}`)
          },
          onError: () => toast.error("Błąd zapisywania"),
        }
      )
    } else {
      createSession.mutate(payload, {
        onSuccess: (res) => {
          toast.success("Szkolenie utworzone")
          navigate(`/training/${res.data.id}`)
        },
        onError: () => toast.error("Błąd tworzenia szkolenia"),
      })
    }
  }

  if (isEditing && isLoading) {
    return (
      <div className="p-6 max-w-[720px] mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[500px]" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[720px] mx-auto">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Wróć
      </Button>

      <h1 className="page-title text-2xl mb-6">
        {isEditing ? "Edytuj szkolenie" : "Nowe szkolenie"}
      </h1>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Nazwa szkolenia *</Label>
            <Input id="name" {...form.register("name")} placeholder="np. Szkolenie sprzedażowe Q1" />
            {form.formState.errors.name && (
              <p className="text-sm text-red-500 mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Opis</Label>
            <Textarea id="description" {...form.register("description")} placeholder="Opis szkolenia..." rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Typ szkolenia *</Label>
              <Select value={form.watch("training_type")} onValueChange={(v) => form.setValue("training_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Wewnętrzne</SelectItem>
                  <SelectItem value="external">Zewnętrzne</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="certification">Certyfikacja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isEditing && (
              <div>
                <Label>Status</Label>
                <Select value={form.watch("status")} onValueChange={(v) => form.setValue("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Zaplanowane</SelectItem>
                    <SelectItem value="in_progress">W trakcie</SelectItem>
                    <SelectItem value="completed">Zakończone</SelectItem>
                    <SelectItem value="cancelled">Anulowane</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="starts_at">Data rozpoczęcia *</Label>
              <Input id="starts_at" type="datetime-local" {...form.register("starts_at")} />
              {form.formState.errors.starts_at && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.starts_at.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="ends_at">Data zakończenia *</Label>
              <Input id="ends_at" type="datetime-local" {...form.register("ends_at")} />
              {form.formState.errors.ends_at && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.ends_at.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location">Lokalizacja</Label>
              <Input id="location" {...form.register("location")} placeholder="np. Sala konferencyjna A" />
            </div>
            <div>
              <Label htmlFor="duration_hours">Czas trwania (godz.)</Label>
              <Input id="duration_hours" type="number" {...form.register("duration_hours")} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="instructor_name">Prowadzący</Label>
              <Input id="instructor_name" {...form.register("instructor_name")} placeholder="Imię i nazwisko" />
            </div>
            <div>
              <Label htmlFor="instructor_email">Email prowadzącego</Label>
              <Input id="instructor_email" type="email" {...form.register("instructor_email")} placeholder="email@example.com" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="max_participants">Maks. uczestników</Label>
              <Input id="max_participants" type="number" {...form.register("max_participants")} />
            </div>
            <div>
              <Label htmlFor="cost_per_person">Koszt / os. (PLN)</Label>
              <Input id="cost_per_person" type="number" step="0.01" {...form.register("cost_per_person")} />
            </div>
          </div>

          <div>
            <Label htmlFor="agenda">Agenda</Label>
            <Textarea id="agenda" {...form.register("agenda")} placeholder="Plan szkolenia..." rows={4} />
          </div>

          <div>
            <Label htmlFor="notes">Notatki</Label>
            <Textarea id="notes" {...form.register("notes")} placeholder="Dodatkowe informacje..." rows={3} />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t">
          <Button type="submit" disabled={createSession.isPending || updateSession.isPending}>
            {createSession.isPending || updateSession.isPending ? "Zapisywanie..." : isEditing ? "Zapisz zmiany" : "Utwórz szkolenie"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Anuluj
          </Button>
        </div>
      </form>
    </div>
  )
}
